import React, { useState } from 'react'
import { Box, Card, CardContent, Grid, Typography } from '@mui/material'
import { ClassNameMap } from '@mui/styles'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import MedicationsComponent from '../common/medications.component'
import DatePickerComponent from '../../../../utils/datepicker.component'
import { useCurrentUser } from '../../../../utils/use-current-user'
import { PatientService } from '../../../../services/patient.service'
import { RootState } from '../../../../redux/store'
import Helper from '../../../../utils/helper'

interface IProps {
  classes: ClassNameMap
  patientId: string
  currentStage: string
  uiResource: any
  handleClose: () => void
  // Parent appends the created med and hides this card.
  handleSubmit: (added: any) => void
}

/**
 * "Add past medication" — a provider records a CLOSED past course (e.g.
 * atorvastatin 40mg Nov 2025 -> Mar 2026) so a lab change can attribute to it.
 *
 * Reuses the shared MedicationsComponent UNCHANGED for the medicine search and
 * dose, and adds the two bounded-window dates here in the wrapper so the 9-way-
 * shared form is not touched. The dates ride into the SAME POST /medications the
 * live add-med card uses; the backend sets a bounded [start, stop] window on a
 * REPORTED med, inert to the clinical engine (atman/clinical/med_history.py).
 */
const AddHistoricalMedicationCard: React.FC<IProps> = ({
  classes,
  patientId,
  currentStage,
  uiResource,
  handleClose,
  handleSubmit,
}) => {
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const { currentRole } = useCurrentUser()
  const patientService = new PatientService()

  const [startedOn, setStartedOn] = useState<number | null>(null)
  const [stoppedOn, setStoppedOn] = useState<number | null>(null)

  // Match the shared form's date->ts convention (noon, to avoid TZ day-shift).
  const toTs = (val: string): number | null =>
    val ? Math.floor(new Date(val + 'T12:00:00').getTime() / 1000) : null

  const getIntakeReg = (reg: string) =>
    Object.keys(uiResource.INTAKE_REGULARITIES || {}).find(
      (key: any) => uiResource.INTAKE_REGULARITIES[key] === reg
    )

  const handleMedSubmit = (
    values: any,
    medicine: any,
    dosageInfo: any,
    _intake: string,
    _type: string,
    _mode: string
  ) => {
    // The window lives in this wrapper, so the shared form's Save can't guard it.
    if (!medicine) {
      toast.error('Select a medication.', Helper.bottom_center())
      return
    }
    if (!startedOn || !stoppedOn) {
      toast.error('A past medication needs both a start and a stop date.', Helper.bottom_center())
      return
    }
    if (startedOn >= stoppedOn) {
      toast.error('The stop date must be after the start date.', Helper.bottom_center())
      return
    }

    const req = {
      med_id: medicine.id,
      med_type: medicine.med_type,
      name: medicine.name,
      num_units: values.num_units,
      delivery_method: values.delivery_method,
      intake_quantity: values.intake_quantity,
      frequency: values.frequency,
      dosage_info: dosageInfo,
      notes: values.note,
      user_role: currentRole,
      prescription_taken: values.prescription_taken,
      // A past course is not being taken now (the backend also enforces this).
      intake_regularity: getIntakeReg('not taking'),
      intake_started_on: startedOn,
      intake_stopped_on: stoppedOn,
    }

    patientService
      .postOtherMedication(patientDetail.id, req)
      .then((response: any) => {
        response?.data && handleSubmit({ ...response.data, mode: 'view' })
      })
      .catch((error: any) => toast.error(Helper.getErrorText(error), Helper.bottom_center()))
  }

  return (
    <Card
      variant='outlined'
      className={[classes.questionCardContent, classes.medIntoleranceCard2].join(' ')}
    >
      <CardContent className={classes.zeroPadding}>
        <Box p={2} pb={0}>
          <Typography variant="subtitle2" className={classes.desc}>
            Add past medication
          </Typography>
          <Typography color="textSecondary" variant="caption">
            A completed course the patient is no longer on — records a bounded
            exposure so lab changes can attribute to it.
          </Typography>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={6}>
              <DatePickerComponent
                label="Start date"
                type="date"
                cssClass={classes.cardLabNotes}
                inputPropCss={classes.cardLabDate}
                variant="outlined"
                value={startedOn ? moment.unix(startedOn).format('YYYY-MM-DD') : ''}
                onCalendarChange={(e: any) => setStartedOn(toTs(e.target.value))}
              />
            </Grid>
            <Grid item xs={6}>
              <DatePickerComponent
                label="Stop date"
                type="date"
                cssClass={classes.cardLabNotes}
                inputPropCss={classes.cardLabDate}
                variant="outlined"
                value={stoppedOn ? moment.unix(stoppedOn).format('YYYY-MM-DD') : ''}
                onCalendarChange={(e: any) => setStoppedOn(toTs(e.target.value))}
              />
            </Grid>
          </Grid>
        </Box>
        <MedicationsComponent
          classes={classes}
          writeMode={true}
          noDropDown=""
          heading=""
          differently={false}
          input=""
          outer=""
          outerSubmit={handleMedSubmit}
          patientId={patientId}
          currentStage={currentStage}
          medicineInfo={null}
          uiResource={uiResource}
          handleClose={handleClose}
        />
      </CardContent>
    </Card>
  )
}

export default AddHistoricalMedicationCard
